import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Toaster, toast } from 'react-hot-toast';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

// ─── GPS Simulation ───────────────────────────────────────────────────────────
function simulateGPSMovement(positions, technicians) {
  const next = { ...positions };
  technicians?.forEach(tech => {
    if (!next?.[tech?.id]) {
      next[tech.id] = { x: 20 + Math.random() * 60, y: 20 + Math.random() * 60 };
    } else if (tech?.status === 'driving') {
      next[tech.id] = {
        x: Math.max(5, Math.min(95, positions?.[tech?.id]?.x + (Math.random() - 0.5) * 6)),
        y: Math.max(5, Math.min(95, positions?.[tech?.id]?.y + (Math.random() - 0.5) * 6)),
      };
    } else if (tech?.status === 'available') {
      next[tech.id] = {
        x: Math.max(5, Math.min(95, positions?.[tech?.id]?.x + (Math.random() - 0.5) * 2.5)),
        y: Math.max(5, Math.min(95, positions?.[tech?.id]?.y + (Math.random() - 0.5) * 2.5)),
      };
    }
  });
  return next;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const PRIORITY_CONFIG = {
  urgent: { label: 'Urgent', color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800', dot: 'bg-red-500', border: 'border-l-red-500' },
  high:   { label: 'High',   color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800', dot: 'bg-orange-500', border: 'border-l-orange-500' },
  normal: { label: 'Normal', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800', dot: 'bg-blue-400', border: 'border-l-blue-400' },
  low:    { label: 'Low',    color: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600', dot: 'bg-slate-400', border: 'border-l-slate-300' },
};

const STATUS_CONFIG = {
  unassigned:  { label: 'Unassigned',  color: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300',   icon: 'Clock' },
  assigned:    { label: 'Assigned',    color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',     icon: 'UserCheck' },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400', icon: 'Zap' },
  completed:   { label: 'Completed',   color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400', icon: 'CheckCircle2' },
  cancelled:   { label: 'Cancelled',   color: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',        icon: 'XCircle' },
};

const TECH_STATUS = {
  available: { label: 'Available', color: 'bg-green-500', text: 'text-green-700 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/30' },
  on_job:    { label: 'On Job',    color: 'bg-amber-500', text: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/30' },
  driving:   { label: 'Driving',   color: 'bg-blue-500',  text: 'text-blue-700 dark:text-blue-400',  bg: 'bg-blue-50 dark:bg-blue-900/30' },
  break:     { label: 'On Break',  color: 'bg-slate-400', text: 'text-slate-600 dark:text-slate-300', bg: 'bg-slate-50 dark:bg-slate-700' },
  offline:   { label: 'Offline',   color: 'bg-red-400',   text: 'text-red-600 dark:text-red-400',    bg: 'bg-red-50 dark:bg-red-900/30' },
};

const TECH_COLORS = ['#22C55E', '#3B82F6', '#F59E0B', '#8B5CF6', '#EF4444', '#06B6D4', '#EC4899', '#F97316'];

const SERVICE_TYPES = ['HVAC', 'Plumbing', 'Electrical', 'Cleaning', 'Roofing', 'Handyman', 'Moving', 'General', 'Other'];

// ─── Helper: format scheduled_time from DB (time type → display string) ───────
function formatTime(val) {
  if (!val) return '—';
  // If it's already a formatted string like "09:00 AM", return as-is
  if (typeof val === 'string' && val?.includes(':')) {
    // Convert HH:MM:SS or HH:MM to 12h format
    const parts = val?.split(':');
    if (parts?.length >= 2) {
      let h = parseInt(parts?.[0]);
      const m = parts?.[1]?.padStart(2, '0');
      const ampm = h >= 12 ? 'PM' : 'AM';
      h = h % 12 || 12;
      return `${h}:${m} ${ampm}`;
    }
  }
  return val;
}

// ─── Helper: map job from DB to dispatch format ───────────────────────────────
function mapJob(j) {
  return {
    id: j?.id,
    title: j?.title || 'Untitled Job',
    customer: j?.customer_name || j?.title || 'Unknown',
    address: j?.address || j?.location || '',
    service: j?.service_type || 'General',
    priority: j?.priority || 'normal',
    status: j?.status === 'scheduled' ? 'unassigned' : (j?.status || 'unassigned'),
    techId: j?.tech_id || null,
    scheduledTime: formatTime(j?.scheduled_time),
    scheduledDate: j?.scheduled_date || '',
    duration: j?.duration || (j?.duration_hours ? `${j?.duration_hours}h` : '1h'),
    value: j?.value ? `$${parseFloat(j?.value)?.toLocaleString()}` : (j?.total_cost ? `$${parseFloat(j?.total_cost)?.toLocaleString()}` : '$0'),
    phone: j?.customer_phone || '',
    notes: j?.notes || '',
    tags: j?.tags || [],
    moveType: j?.move_type || 'local',
    cubicFeet: j?.cubic_feet || null,
    miles: j?.miles || null,
    _raw: j,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────
const StatBadge = ({ icon, label, value, color }) => (
  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${color}`}>
    <Icon name={icon} size={14} />
    <span className="text-xs font-medium">{label}</span>
    <span className="text-sm font-semibold">{value}</span>
  </div>
);

const JobCard = ({ job, onDragStart, onDragEnd, onClick, isDragging }) => {
  const priority = PRIORITY_CONFIG?.[job?.priority] || PRIORITY_CONFIG?.normal;
  const status = STATUS_CONFIG?.[job?.status] || STATUS_CONFIG?.unassigned;
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, job)}
      onDragEnd={onDragEnd}
      onClick={() => onClick(job)}
      className={`group relative bg-card rounded-xl border border-border shadow-sm cursor-grab active:cursor-grabbing select-none border-l-4 ${priority?.border} transition-all duration-150 hover:shadow-md hover:-translate-y-0.5 ${isDragging ? 'opacity-40 scale-95' : 'opacity-100'}`}
    >
      <div className="p-3">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-card-foreground leading-tight truncate">{job?.title}</p>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">{job?.customer}</p>
          </div>
          <span className={`shrink-0 text-xs font-medium px-1.5 py-0.5 rounded-md border ${priority?.color}`}>{priority?.label}</span>
        </div>
        {job?.address && (
          <div className="flex items-center gap-1.5 mb-2">
            <Icon name="MapPin" size={11} className="text-muted-foreground shrink-0" />
            <p className="text-xs text-muted-foreground truncate">{job?.address}</p>
          </div>
        )}
        <div className="flex items-center gap-3 mb-2">
          <div className="flex items-center gap-1">
            <Icon name="Clock" size={11} className="text-muted-foreground" />
            <span className="text-xs text-foreground font-medium">{job?.scheduledTime}</span>
          </div>
          <div className="flex items-center gap-1">
            <Icon name="Timer" size={11} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{job?.duration}</span>
          </div>
          <div className="ml-auto flex items-center gap-1">
            <Icon name="DollarSign" size={11} className="text-green-500" />
            <span className="text-xs font-semibold text-green-600 dark:text-green-400">{job?.value}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-md font-medium">{job?.service}</span>
          <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${status?.color}`}>{status?.label}</span>
          {job?.moveType === 'long_distance' && (
            <span className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 px-2 py-0.5 rounded-md font-medium">Long Distance</span>
          )}
        </div>
      </div>
    </div>
  );
};

const TechnicianColumn = ({ tech, jobs, onDragStart, onDragEnd, onDragOver, onDrop, onJobClick, draggingJobId }) => {
  const statusCfg = TECH_STATUS?.[tech?.status] || TECH_STATUS?.offline;
  const techJobs = jobs?.filter(j => j?.techId === tech?.id);
  const totalValue = techJobs?.reduce((sum, j) => sum + parseInt(j?.value?.replace(/\D/g, '') || '0'), 0);
  return (
    <div
      className="flex flex-col bg-muted/30 rounded-2xl border border-border min-w-[280px] max-w-[280px] overflow-hidden"
      onDragOver={(e) => { e?.preventDefault(); onDragOver(tech?.id); }}
      onDrop={(e) => onDrop(e, tech?.id)}
    >
      <div className="bg-card border-b border-border p-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold shrink-0 shadow-sm" style={{ backgroundColor: tech?.color }}>
            {tech?.avatar}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-card-foreground truncate">{tech?.name}</p>
              <span className={`inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded-full ${statusCfg?.bg} ${statusCfg?.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${statusCfg?.color}`} />
                {statusCfg?.label}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">{tech?.role}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-2.5 pt-2.5 border-t border-border">
          <div className="flex items-center gap-1">
            <Icon name="Star" size={11} className="text-amber-400 fill-amber-400" />
            <span className="text-xs font-semibold text-foreground">{tech?.rating}</span>
          </div>
          <div className="flex items-center gap-1">
            <Icon name="Briefcase" size={11} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{techJobs?.length} jobs</span>
          </div>
          <div className="flex items-center gap-1 ml-auto">
            <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-green-500 transition-all" style={{ width: `${tech?.efficiency}%` }} />
            </div>
            <span className="text-xs font-medium text-muted-foreground">{tech?.efficiency}%</span>
          </div>
        </div>
      </div>
      <div className="flex-1 p-2 space-y-2 min-h-[200px] overflow-y-auto">
        {techJobs?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-border rounded-xl text-muted-foreground">
            <Icon name="Plus" size={20} className="mb-1" />
            <p className="text-xs">Drop job here</p>
          </div>
        ) : (
          techJobs?.map(job => (
            <JobCard
              key={job?.id}
              job={job}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onClick={onJobClick}
              isDragging={draggingJobId === job?.id}
            />
          ))
        )}
      </div>
      <div className="bg-card border-t border-border px-3 py-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{techJobs?.filter(j => j?.status === 'completed')?.length}/{techJobs?.length} done</span>
          <span className="text-xs font-semibold text-green-600 dark:text-green-400">${totalValue?.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
};

const UnassignedColumn = ({ jobs, onDragStart, onDragEnd, onDragOver, onDrop, onJobClick, draggingJobId }) => {
  const unassigned = jobs?.filter(j => !j?.techId);
  return (
    <div
      className="flex flex-col bg-red-50 dark:bg-red-900/20 rounded-2xl border border-red-200 dark:border-red-800 min-w-[280px] max-w-[280px] overflow-hidden"
      onDragOver={(e) => { e?.preventDefault(); onDragOver(null); }}
      onDrop={(e) => onDrop(e, null)}
    >
      <div className="bg-card border-b border-red-200 dark:border-red-800 p-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <Icon name="AlertCircle" size={16} className="text-red-500" />
          </div>
          <div>
            <p className="text-sm font-semibold text-card-foreground">Unassigned</p>
            <p className="text-xs text-red-500 dark:text-red-400 font-medium">{unassigned?.length} jobs need dispatch</p>
          </div>
        </div>
      </div>
      <div className="flex-1 p-2 space-y-2 min-h-[200px] overflow-y-auto">
        {unassigned?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-24 text-muted-foreground">
            <Icon name="CheckCircle2" size={20} className="mb-1 text-green-400" />
            <p className="text-xs text-green-600 dark:text-green-400 font-medium">All jobs assigned!</p>
          </div>
        ) : (
          unassigned?.map(job => (
            <JobCard key={job?.id} job={job} onDragStart={onDragStart} onDragEnd={onDragEnd} onClick={onJobClick} isDragging={draggingJobId === job?.id} />
          ))
        )}
      </div>
    </div>
  );
};

// ─── Add/Edit Job Modal ───────────────────────────────────────────────────────
const JobModal = ({ job, technicians, orgId, onClose, onSaved }) => {
  const isEdit = !!job;
  const [form, setForm] = useState({
    title: job?.title || '',
    customer_name: job?.customer || '',
    customer_phone: job?.phone || '',
    address: job?.address || '',
    service_type: job?.service || 'General',
    priority: job?.priority || 'normal',
    status: job?._raw?.status || 'scheduled',
    scheduled_date: job?.scheduledDate || '',
    scheduled_time: job?.scheduledTime !== '—' ? job?.scheduledTime : '',
    duration: job?.duration || '1h',
    value: job?.value ? job?.value?.replace(/\D/g, '') : '',
    notes: job?.notes || '',
    tech_id: job?.techId || '',
    move_type: job?.moveType || 'local',
    cubic_feet: job?.cubicFeet || '',
    miles: job?.miles || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form?.title?.trim()) { toast?.error('Job title required'); return; }
    setSaving(true);
    try {
      // Convert time string to HH:MM format for DB
      let dbTime = null;
      if (form?.scheduled_time) {
        const t = form?.scheduled_time?.trim();
        // Accept HH:MM, H:MM AM/PM formats
        const ampmMatch = t?.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
        if (ampmMatch) {
          let h = parseInt(ampmMatch?.[1]);
          const m = ampmMatch?.[2];
          const ampm = ampmMatch?.[3]?.toUpperCase();
          if (ampm === 'PM' && h !== 12) h += 12;
          if (ampm === 'AM' && h === 12) h = 0;
          dbTime = `${String(h)?.padStart(2, '0')}:${m}:00`;
        } else if (t?.match(/^\d{1,2}:\d{2}$/)) {
          dbTime = `${t}:00`;
        } else {
          dbTime = t;
        }
      }

      const payload = {
        title: form?.title,
        customer_name: form?.customer_name,
        customer_phone: form?.customer_phone,
        address: form?.address,
        service_type: form?.service_type,
        priority: form?.priority,
        status: form?.tech_id ? 'in_progress' : 'scheduled',
        scheduled_date: form?.scheduled_date || null,
        scheduled_time: dbTime,
        duration: form?.duration,
        value: parseFloat(form?.value) || 0,
        notes: form?.notes,
        tech_id: form?.tech_id || null,
        org_id: orgId,
        move_type: form?.move_type,
        cubic_feet: form?.move_type === 'long_distance' && form?.cubic_feet ? parseFloat(form?.cubic_feet) : null,
        miles: form?.move_type === 'long_distance' && form?.miles ? parseFloat(form?.miles) : null,
      };

      if (isEdit) {
        const { error } = await supabase?.from('jobs')?.update(payload)?.eq('id', job?.id);
        if (error) throw error;
        toast?.success('Job updated');
      } else {
        const { error } = await supabase?.from('jobs')?.insert(payload);
        if (error) throw error;
        toast?.success('Job created');
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast?.error(err?.message || 'Failed to save job');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-card z-10">
          <h3 className="text-base font-semibold text-foreground">{isEdit ? 'Edit Job' : 'New Job'}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5">Job Title *</label>
            <input type="text" value={form?.title} onChange={e => setForm(p => ({ ...p, title: e?.target?.value }))}
              placeholder="e.g. AC Unit Replacement"
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Customer Name</label>
              <input type="text" value={form?.customer_name} onChange={e => setForm(p => ({ ...p, customer_name: e?.target?.value }))}
                placeholder="John Smith"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Phone</label>
              <input type="text" value={form?.customer_phone} onChange={e => setForm(p => ({ ...p, customer_phone: e?.target?.value }))}
                placeholder="(555) 000-0000"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5">Address</label>
            <input type="text" value={form?.address} onChange={e => setForm(p => ({ ...p, address: e?.target?.value }))}
              placeholder="123 Main St, City, State"
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Service Type</label>
              <select value={form?.service_type} onChange={e => setForm(p => ({ ...p, service_type: e?.target?.value }))}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
                {SERVICE_TYPES?.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Priority</label>
              <select value={form?.priority} onChange={e => setForm(p => ({ ...p, priority: e?.target?.value }))}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
                {Object.entries(PRIORITY_CONFIG)?.map(([k, v]) => <option key={k} value={k}>{v?.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Date</label>
              <input type="date" value={form?.scheduled_date} onChange={e => setForm(p => ({ ...p, scheduled_date: e?.target?.value }))}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Time</label>
              <input type="time" value={
                (() => {
                  const t = form?.scheduled_time;
                  if (!t) return '';
                  const ampmMatch = t?.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
                  if (ampmMatch) {
                    let h = parseInt(ampmMatch?.[1]);
                    const m = ampmMatch?.[2];
                    const ampm = ampmMatch?.[3]?.toUpperCase();
                    if (ampm === 'PM' && h !== 12) h += 12;
                    if (ampm === 'AM' && h === 12) h = 0;
                    return `${String(h)?.padStart(2,'0')}:${m}`;
                  }
                  return t?.slice(0, 5);
                })()
              } onChange={e => {
                const val = e?.target?.value; // HH:MM
                if (!val) { setForm(p => ({ ...p, scheduled_time: '' })); return; }
                const [h, m] = val?.split(':')?.map(Number);
                const ampm = h >= 12 ? 'PM' : 'AM';
                const h12 = h % 12 || 12;
                setForm(p => ({ ...p, scheduled_time: `${h12}:${String(m)?.padStart(2,'0')} ${ampm}` }));
              }}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Duration</label>
              <input type="text" value={form?.duration} onChange={e => setForm(p => ({ ...p, duration: e?.target?.value }))}
                placeholder="e.g. 2h 30m"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Value ($)</label>
              <input type="number" value={form?.value} onChange={e => setForm(p => ({ ...p, value: e?.target?.value }))}
                placeholder="500"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
          </div>

          {/* Move Type */}
          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5">Move Type</label>
            <div className="flex gap-2">
              {[{ key: 'local', label: 'Local' }, { key: 'long_distance', label: 'Long Distance' }]?.map(opt => (
                <button key={opt?.key} type="button"
                  onClick={() => setForm(p => ({ ...p, move_type: opt?.key }))}
                  className={`flex-1 py-2 rounded-lg border text-xs font-semibold transition-colors ${form?.move_type === opt?.key ? 'bg-primary text-white border-primary' : 'border-border text-muted-foreground hover:bg-muted'}`}>
                  {opt?.label}
                </button>
              ))}
            </div>
          </div>
          {form?.move_type === 'long_distance' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">Cubic Feet (CF)</label>
                <input type="number" value={form?.cubic_feet} onChange={e => setForm(p => ({ ...p, cubic_feet: e?.target?.value }))}
                  placeholder="e.g. 800"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">Miles</label>
                <input type="number" value={form?.miles} onChange={e => setForm(p => ({ ...p, miles: e?.target?.value }))}
                  placeholder="e.g. 1200"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5">Assign Technician</label>
            <select value={form?.tech_id} onChange={e => setForm(p => ({ ...p, tech_id: e?.target?.value }))}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
              <option value="">Unassigned</option>
              {technicians?.map(t => <option key={t?.id} value={t?.id}>{t?.name} ({TECH_STATUS?.[t?.status]?.label})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5">Notes</label>
            <textarea value={form?.notes} onChange={e => setForm(p => ({ ...p, notes: e?.target?.value }))}
              placeholder="Additional notes..."
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
          </div>
        </div>
        <div className="flex gap-3 p-5 border-t border-border">
          <button onClick={onClose} className="flex-1 py-2.5 border border-border rounded-xl text-sm font-medium hover:bg-muted transition-colors">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2.5 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors">
            {saving ? 'Saving...' : isEdit ? 'Update Job' : 'Create Job'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Add/Edit Technician Modal ────────────────────────────────────────────────
const TechnicianModal = ({ tech, orgId, onClose, onSaved }) => {
  const isEdit = !!tech;
  const [form, setForm] = useState({
    name: tech?.name || '',
    role: tech?.role || 'Technician',
    phone: tech?.phone || '',
    status: tech?.status || 'available',
    rating: tech?.rating || 5.0,
    efficiency: tech?.efficiency || 90,
    skills: tech?.skills?.join(', ') || '',
    color: tech?.color || TECH_COLORS?.[0],
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form?.name?.trim()) { toast?.error('Name required'); return; }
    setSaving(true);
    try {
      const initials = form?.name?.split(' ')?.map(w => w?.[0])?.join('')?.toUpperCase()?.slice(0, 2);
      const payload = {
        name: form?.name,
        avatar: initials,
        role: form?.role,
        phone: form?.phone,
        status: form?.status,
        rating: parseFloat(form?.rating) || 5.0,
        efficiency: parseInt(form?.efficiency) || 90,
        skills: form?.skills ? form?.skills?.split(',')?.map(s => s?.trim())?.filter(Boolean) : [],
        color: form?.color,
        org_id: orgId,
      };
      if (isEdit) {
        const { error } = await supabase?.from('technicians')?.update(payload)?.eq('id', tech?.id);
        if (error) throw error;
        toast?.success('Technician updated');
      } else {
        const { error } = await supabase?.from('technicians')?.insert(payload);
        if (error) throw error;
        toast?.success('Technician added');
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast?.error(err?.message || 'Failed to save technician');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-card z-10">
          <h3 className="text-base font-semibold text-foreground">{isEdit ? 'Edit Technician' : 'Add Technician'}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Full Name *</label>
              <input type="text" value={form?.name} onChange={e => setForm(p => ({ ...p, name: e?.target?.value }))}
                placeholder="John Smith"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Role</label>
              <input type="text" value={form?.role} onChange={e => setForm(p => ({ ...p, role: e?.target?.value }))}
                placeholder="Senior Tech"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Phone</label>
              <input type="text" value={form?.phone} onChange={e => setForm(p => ({ ...p, phone: e?.target?.value }))}
                placeholder="(555) 000-0000"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Status</label>
              <select value={form?.status} onChange={e => setForm(p => ({ ...p, status: e?.target?.value }))}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
                {Object.entries(TECH_STATUS)?.map(([k, v]) => <option key={k} value={k}>{v?.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Rating (0–5)</label>
              <input type="number" min="0" max="5" step="0.1" value={form?.rating} onChange={e => setForm(p => ({ ...p, rating: e?.target?.value }))}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Efficiency (%)</label>
              <input type="number" min="0" max="100" value={form?.efficiency} onChange={e => setForm(p => ({ ...p, efficiency: e?.target?.value }))}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5">Skills (comma-separated)</label>
            <input type="text" value={form?.skills} onChange={e => setForm(p => ({ ...p, skills: e?.target?.value }))}
              placeholder="HVAC, Electrical, Plumbing"
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5">Color</label>
            <div className="flex gap-2 flex-wrap">
              {TECH_COLORS?.map(c => (
                <button key={c} type="button" onClick={() => setForm(p => ({ ...p, color: c }))}
                  className={`w-7 h-7 rounded-lg border-2 transition-all ${form?.color === c ? 'border-foreground scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-3 p-5 border-t border-border">
          <button onClick={onClose} className="flex-1 py-2.5 border border-border rounded-xl text-sm font-medium hover:bg-muted transition-colors">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2.5 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors">
            {saving ? 'Saving...' : isEdit ? 'Update' : 'Add Technician'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Job Detail Drawer ────────────────────────────────────────────────────────
const JobDetailDrawer = ({ job, technicians, onClose, onAssign, onStatusChange, onEdit, onDelete }) => {
  if (!job) return null;
  const priority = PRIORITY_CONFIG?.[job?.priority] || PRIORITY_CONFIG?.normal;
  const status = STATUS_CONFIG?.[job?.status] || STATUS_CONFIG?.unassigned;
  const assignedTech = technicians?.find(t => t?.id === job?.techId);
  const [confirmDel, setConfirmDel] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-[420px] bg-card h-full shadow-2xl flex flex-col overflow-hidden border-l border-border">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h2 className="text-base font-semibold text-card-foreground">{job?.title}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Job Details</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => onEdit?.(job)} className="p-2 rounded-lg hover:bg-muted transition-colors" title="Edit">
              <Icon name="Pencil" size={15} className="text-muted-foreground" />
            </button>
            <button onClick={() => setConfirmDel(true)} className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" title="Delete">
              <Icon name="Trash2" size={15} className="text-red-500" />
            </button>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
              <Icon name="X" size={18} className="text-muted-foreground" />
            </button>
          </div>
        </div>

        {confirmDel && (
          <div className="mx-4 mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
            <p className="text-sm font-medium text-red-700 dark:text-red-400 mb-3">Delete this job?</p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmDel(false)} className="flex-1 py-1.5 border border-border rounded-lg text-xs font-medium hover:bg-muted transition-colors">Cancel</button>
              <button onClick={() => onDelete?.(job?.id)} className="flex-1 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 transition-colors">Delete</button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-medium px-2.5 py-1 rounded-lg border ${priority?.color}`}>
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${priority?.dot} mr-1.5`} />
              {priority?.label} Priority
            </span>
            <span className={`text-xs font-medium px-2.5 py-1 rounded-lg ${status?.color}`}>{status?.label}</span>
            {job?.moveType === 'long_distance' && (
              <span className="text-xs font-medium px-2.5 py-1 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">Long Distance</span>
            )}
          </div>

          {job?.customer && (
            <div className="bg-muted/50 rounded-xl p-4 space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Customer</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-white text-sm font-bold">
                  {job?.customer?.charAt(0)}
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{job?.customer}</p>
                  {job?.phone && (
                    <a href={`tel:${job?.phone}`} className="text-xs text-primary hover:underline">{job?.phone}</a>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Job Details</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Service Type</p>
                <p className="text-sm font-semibold text-foreground">{job?.service}</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Value</p>
                <p className="text-sm font-semibold text-green-600 dark:text-green-400">{job?.value}</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Scheduled</p>
                <p className="text-sm font-semibold text-foreground">
                  {job?.scheduledDate ? `${job?.scheduledDate} ` : ''}{job?.scheduledTime}
                </p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Duration</p>
                <p className="text-sm font-semibold text-foreground">{job?.duration}</p>
              </div>
            </div>

            {/* Long Distance fields */}
            {job?.moveType === 'long_distance' && (job?.cubicFeet || job?.miles) && (
              <div className="grid grid-cols-2 gap-3">
                {job?.cubicFeet && (
                  <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3">
                    <p className="text-xs text-orange-600 dark:text-orange-400 mb-1">Cubic Feet (CF)</p>
                    <p className="text-sm font-bold text-orange-700 dark:text-orange-300">{job?.cubicFeet} CF</p>
                  </div>
                )}
                {job?.miles && (
                  <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3">
                    <p className="text-xs text-orange-600 dark:text-orange-400 mb-1">Miles</p>
                    <p className="text-sm font-bold text-orange-700 dark:text-orange-300">{job?.miles} mi</p>
                  </div>
                )}
              </div>
            )}

            {job?.address && (
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Address</p>
                <div className="flex items-center gap-2">
                  <Icon name="MapPin" size={13} className="text-muted-foreground shrink-0" />
                  <p className="text-sm text-foreground">{job?.address}</p>
                </div>
              </div>
            )}
            {job?.notes && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <p className="text-xs text-amber-600 dark:text-amber-400 font-semibold mb-1">Notes</p>
                <p className="text-sm text-amber-800 dark:text-amber-300">{job?.notes}</p>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Assigned Technician</p>
            {assignedTech ? (
              <div className="flex items-center gap-3 bg-muted/50 rounded-xl p-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: assignedTech?.color }}>
                  {assignedTech?.avatar}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">{assignedTech?.name}</p>
                  <p className="text-xs text-muted-foreground">{assignedTech?.role} · ⭐ {assignedTech?.rating}</p>
                </div>
                <div className="flex items-center gap-2">
                  {assignedTech?.phone && (
                    <a href={`tel:${assignedTech?.phone}`} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
                      <Icon name="Phone" size={13} className="text-muted-foreground" />
                    </a>
                  )}
                  <button onClick={() => onAssign(job?.id, null)} className="text-xs text-red-500 hover:text-red-700 font-medium transition-colors">Unassign</button>
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground bg-muted/50 rounded-xl p-3 text-center">No technician assigned</div>
            )}
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Quick Assign:</p>
              <div className="flex flex-wrap gap-2">
                {technicians?.filter(t => t?.id !== job?.techId)?.map(t => {
                  const tStatus = TECH_STATUS?.[t?.status];
                  return (
                    <button key={t?.id} onClick={() => onAssign(job?.id, t?.id)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border hover:border-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors text-xs font-medium text-foreground">
                      <span className="w-5 h-5 rounded-md flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: t?.color }}>
                        {t?.avatar?.charAt(0)}
                      </span>
                      {t?.name?.split(' ')?.[0]}
                      <span className={`text-xs ${tStatus?.text}`}>· {tStatus?.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Update Status</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(STATUS_CONFIG)?.map(([key, cfg]) => (
                <button key={key} onClick={() => onStatusChange(job?.id, key)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    job?.status === key ? `${cfg?.color} border-current` : 'border-border text-muted-foreground hover:bg-muted'
                  }`}>
                  <Icon name={cfg?.icon} size={13} />
                  {cfg?.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Map View ─────────────────────────────────────────────────────────────────
const MapView = ({ jobs, technicians, gpsPositions, countdown, onManualRefresh, isRefreshing }) => {
  const [hoveredId, setHoveredId] = useState(null);

  return (
    <div className="relative bg-muted/50 rounded-2xl overflow-hidden border border-border" style={{ height: '460px' }}>
      <div className="absolute inset-0" style={{
        backgroundImage: `linear-gradient(rgba(148,163,184,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.15) 1px, transparent 1px)`,
        backgroundSize: '40px 40px'
      }} />
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        <line x1="0" y1="45" x2="100" y2="45" stroke="#CBD5E1" strokeWidth="0.8" />
        <line x1="0" y1="70" x2="100" y2="70" stroke="#CBD5E1" strokeWidth="0.8" />
        <line x1="30" y1="0" x2="30" y2="100" stroke="#CBD5E1" strokeWidth="0.8" />
        <line x1="60" y1="0" x2="60" y2="100" stroke="#CBD5E1" strokeWidth="0.8" />
      </svg>
      {/* Job pins */}
      {jobs?.filter(j => j?.address && j?.techId)?.slice(0, 8)?.map((job, idx) => {
        const tech = technicians?.find(t => t?.id === job?.techId);
        const x = 15 + (idx * 11) % 70;
        const y = 25 + (idx * 17) % 55;
        return (
          <div key={job?.id} className="absolute transform -translate-x-1/2 -translate-y-1/2 z-10"
            style={{ left: `${x}%`, top: `${y}%` }}>
            <div className="w-3 h-3 rounded-full border-2 border-white shadow-md"
              style={{ backgroundColor: tech?.color || '#94A3B8' }} />
          </div>
        );
      })}
      {/* Technician pins */}
      {technicians?.map(tech => {
        const pos = gpsPositions?.[tech?.id];
        if (!pos) return null;
        const statusCfg = TECH_STATUS?.[tech?.status] || TECH_STATUS?.offline;
        return (
          <div key={tech?.id}
            className="absolute transform -translate-x-1/2 -translate-y-1/2 group cursor-pointer z-20"
            style={{ left: `${pos?.x}%`, top: `${pos?.y}%`, transition: 'left 1.4s cubic-bezier(0.4,0,0.2,1), top 1.4s cubic-bezier(0.4,0,0.2,1)' }}
            onMouseEnter={() => setHoveredId(tech?.id)}
            onMouseLeave={() => setHoveredId(null)}
          >
            <div className="relative">
              {tech?.status === 'driving' && (
                <span className="absolute rounded-full animate-ping opacity-30" style={{ backgroundColor: tech?.color, width: '36px', height: '36px', top: '-4px', left: '-4px' }} />
              )}
              <div className="w-9 h-9 rounded-full border-2 border-white shadow-lg flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: tech?.color }}>
                {tech?.avatar}
              </div>
              <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${statusCfg?.color}`} />
            </div>
            {hoveredId === tech?.id && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1.5 bg-slate-800 text-white text-xs rounded-lg whitespace-nowrap z-30 shadow-lg pointer-events-none">
                <p className="font-semibold">{tech?.name}</p>
                <p className="text-slate-300">{tech?.role}</p>
                <p className="font-medium" style={{ color: tech?.color }}>{statusCfg?.label}</p>
                <p className="text-slate-400">{jobs?.filter(j => j?.techId === tech?.id)?.length} jobs today</p>
              </div>
            )}
          </div>
        );
      })}
      <div className="absolute top-3 left-3 flex items-center gap-2 bg-card/95 backdrop-blur-sm px-3 py-2 rounded-xl shadow-sm border border-border">
        <span className={`w-2 h-2 rounded-full ${isRefreshing ? 'bg-amber-400' : 'bg-green-500 animate-pulse'}`} />
        <span className="text-xs font-semibold text-foreground">Live GPS</span>
        <span className="text-xs text-muted-foreground">·</span>
        <span className="text-xs text-muted-foreground tabular-nums">{isRefreshing ? 'Updating...' : `Refresh in ${countdown}s`}</span>
        <button onClick={onManualRefresh} className="ml-1 p-1 rounded-md hover:bg-muted transition-colors">
          <Icon name="RefreshCw" size={11} className={`text-muted-foreground ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>
      {/* Legend */}
      <div className="absolute bottom-3 left-3 flex items-center gap-3 bg-card/95 backdrop-blur-sm px-3 py-2 rounded-xl shadow-sm border border-border">
        {Object.entries(TECH_STATUS)?.slice(0, 4)?.map(([k, v]) => (
          <div key={k} className="flex items-center gap-1">
            <span className={`w-2 h-2 rounded-full ${v?.color}`} />
            <span className="text-xs text-muted-foreground">{v?.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Main Dispatch Page ───────────────────────────────────────────────────────
const DispatchPage = () => {
  const { profile } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggingJob, setDraggingJob] = useState(null);
  const [dragOverTech, setDragOverTech] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [editingJob, setEditingJob] = useState(null);
  const [editingTech, setEditingTech] = useState(null);
  const [activeView, setActiveView] = useState('board');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterTech, setFilterTech] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddJobModal, setShowAddJobModal] = useState(false);
  const [showAddTechModal, setShowAddTechModal] = useState(false);
  const [showTechManage, setShowTechManage] = useState(false);
  const [confirmDeleteTech, setConfirmDeleteTech] = useState(null);

  // GPS state
  const [gpsPositions, setGpsPositions] = useState({});
  const [countdown, setCountdown] = useState(30);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const techsRef = useRef(technicians);
  useEffect(() => { techsRef.current = technicians; }, [technicians]);

  const fetchData = useCallback(async () => {
    if (!profile?.org_id) return;
    setLoading(true);
    const [jobsRes, techsRes] = await Promise.all([
      supabase?.from('jobs')?.select('*')?.eq('org_id', profile?.org_id)?.is('deleted_at', null)?.order('created_at', { ascending: false }),
      supabase?.from('technicians')?.select('*')?.eq('org_id', profile?.org_id)?.order('created_at', { ascending: true }),
    ]);
    if (!jobsRes?.error) setJobs((jobsRes?.data || [])?.map(mapJob));
    if (!techsRes?.error) {
      const techs = techsRes?.data || [];
      setTechnicians(techs);
      setGpsPositions(prev => {
        const next = { ...prev };
        techs?.forEach(t => {
          if (!next?.[t?.id]) next[t.id] = { x: 20 + Math.random() * 60, y: 20 + Math.random() * 60 };
        });
        return next;
      });
    }
    setLoading(false);
  }, [profile?.org_id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Realtime subscription
  useEffect(() => {
    if (!profile?.org_id) return;
    const channel = supabase?.channel('dispatch-realtime')?.on('postgres_changes', { event: '*', schema: 'public', table: 'jobs', filter: `org_id=eq.${profile?.org_id}` }, () => {
        fetchData();
      })?.on('postgres_changes', { event: '*', schema: 'public', table: 'technicians', filter: `org_id=eq.${profile?.org_id}` }, () => {
        fetchData();
      })?.subscribe();
    return () => { supabase?.removeChannel(channel); };
  }, [profile?.org_id, fetchData]);

  const doRefresh = useCallback(() => {
    setIsRefreshing(true);
    setTimeout(() => {
      setGpsPositions(prev => simulateGPSMovement(prev, techsRef?.current));
      setCountdown(30);
      setIsRefreshing(false);
    }, 600);
  }, []);

  useEffect(() => {
    refreshIntervalRef.current = setInterval(doRefresh, 30000);
    return () => clearInterval(refreshIntervalRef?.current);
  }, [doRefresh]);

  useEffect(() => {
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => (prev <= 1 ? 30 : prev - 1));
    }, 1000);
    return () => clearInterval(countdownIntervalRef?.current);
  }, []);

  // Stats
  const totalJobs = jobs?.length;
  const completedJobs = jobs?.filter(j => j?.status === 'completed')?.length;
  const inProgressJobs = jobs?.filter(j => j?.status === 'in_progress')?.length;
  const unassignedJobs = jobs?.filter(j => !j?.techId)?.length;
  const urgentJobs = jobs?.filter(j => j?.priority === 'urgent')?.length;
  const totalRevenue = jobs?.reduce((sum, j) => sum + parseInt(j?.value?.replace(/\D/g, '') || '0'), 0);
  const availableTechs = technicians?.filter(t => t?.status === 'available')?.length;

  // Drag handlers
  const handleDragStart = useCallback((e, job) => {
    setDraggingJob(job);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggingJob(null);
    setDragOverTech(null);
  }, []);

  const handleDragOver = useCallback((techId) => {
    setDragOverTech(techId);
  }, []);

  const handleDrop = useCallback(async (e, techId) => {
    e?.preventDefault();
    if (!draggingJob) return;
    const newStatus = techId ? 'assigned' : 'unassigned';
    const dbStatus = techId ? 'in_progress' : 'scheduled';
    try {
      const { error } = await supabase?.from('jobs')?.update({ tech_id: techId || null, status: dbStatus })?.eq('id', draggingJob?.id);
      if (error) throw error;
      setJobs(prev => prev?.map(j =>
        j?.id === draggingJob?.id ? { ...j, techId, status: newStatus } : j
      ));
      toast?.success(techId ? 'Job assigned' : 'Job unassigned');
    } catch (err) {
      toast?.error('Failed to assign job');
    }
    setDraggingJob(null);
    setDragOverTech(null);
  }, [draggingJob]);

  const handleAssign = useCallback(async (jobId, techId) => {
    const newStatus = techId ? 'assigned' : 'unassigned';
    const dbStatus = techId ? 'in_progress' : 'scheduled';
    try {
      const { error } = await supabase?.from('jobs')?.update({ tech_id: techId || null, status: dbStatus })?.eq('id', jobId);
      if (error) throw error;
      setJobs(prev => prev?.map(j => j?.id === jobId ? { ...j, techId, status: newStatus } : j));
      setSelectedJob(prev => prev ? { ...prev, techId, status: newStatus } : null);
      toast?.success(techId ? 'Technician assigned' : 'Technician unassigned');
    } catch (err) {
      toast?.error('Failed to assign job');
    }
  }, []);

  const handleStatusChange = useCallback(async (jobId, status) => {
    const dbStatus = status === 'unassigned' ? 'scheduled' : status;
    try {
      const { error } = await supabase?.from('jobs')?.update({ status: dbStatus })?.eq('id', jobId);
      if (error) throw error;
      setJobs(prev => prev?.map(j => j?.id === jobId ? { ...j, status } : j));
      setSelectedJob(prev => prev ? { ...prev, status } : null);
      toast?.success('Status updated');
    } catch (err) {
      toast?.error('Failed to update status');
    }
  }, []);

  const handleDeleteJob = useCallback(async (jobId) => {
    try {
      const { error } = await supabase?.from('jobs')?.update({ deleted_at: new Date()?.toISOString() })?.eq('id', jobId);
      if (error) throw error;
      setJobs(prev => prev?.filter(j => j?.id !== jobId));
      setSelectedJob(null);
      toast?.success('Job deleted');
    } catch (err) {
      toast?.error('Failed to delete job');
    }
  }, []);

  const handleDeleteTech = async (techId) => {
    try {
      const { error } = await supabase?.from('technicians')?.delete()?.eq('id', techId);
      if (error) throw error;
      setTechnicians(prev => prev?.filter(t => t?.id !== techId));
      setConfirmDeleteTech(null);
      toast?.success('Technician removed');
    } catch (err) {
      toast?.error('Failed to remove technician');
    }
  };

  const handleTechStatusChange = async (techId, newStatus) => {
    try {
      const { error } = await supabase?.from('technicians')?.update({ status: newStatus })?.eq('id', techId);
      if (error) throw error;
      setTechnicians(prev => prev?.map(t => t?.id === techId ? { ...t, status: newStatus } : t));
      toast?.success('Status updated');
    } catch (err) {
      toast?.error('Failed to update status');
    }
  };

  // Filtered jobs (applied to all views)
  const filteredJobs = jobs?.filter(j => {
    if (filterStatus !== 'all' && j?.status !== filterStatus) return false;
    if (filterPriority !== 'all' && j?.priority !== filterPriority) return false;
    if (filterTech !== 'all') {
      if (filterTech === 'unassigned' && j?.techId) return false;
      if (filterTech !== 'unassigned' && j?.techId !== filterTech) return false;
    }
    if (searchQuery) {
      const q = searchQuery?.toLowerCase();
      if (!j?.title?.toLowerCase()?.includes(q) &&
          !j?.customer?.toLowerCase()?.includes(q) &&
          !j?.address?.toLowerCase()?.includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <Helmet><title>Dispatch Center — Wilcart CRM</title></Helmet>
      <Toaster position="top-right" />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0 lg:ml-64">
        <Header onMenuToggle={() => setIsSidebarOpen(true)} title="Dispatch Center" />

        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Stats Bar */}
          <div className="bg-card border-b border-border px-6 py-3">
            <div className="flex items-center gap-3 flex-wrap">
              <StatBadge icon="Briefcase" label="Total" value={totalJobs} color="bg-muted border-border text-muted-foreground" />
              <StatBadge icon="AlertCircle" label="Unassigned" value={unassignedJobs} color="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400" />
              <StatBadge icon="Zap" label="In Progress" value={inProgressJobs} color="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-600 dark:text-amber-400" />
              <StatBadge icon="CheckCircle2" label="Completed" value={completedJobs} color="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-600 dark:text-green-400" />
              <StatBadge icon="AlertTriangle" label="Urgent" value={urgentJobs} color="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400" />
              <StatBadge icon="Users" label="Available" value={`${availableTechs}/${technicians?.length}`} color="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-600 dark:text-green-400" />
              <div className="ml-auto flex items-center gap-1.5 px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <Icon name="DollarSign" size={14} className="text-green-600 dark:text-green-400" />
                <span className="text-xs font-medium text-green-700 dark:text-green-400">Revenue</span>
                <span className="text-sm font-bold text-green-700 dark:text-green-400">${totalRevenue?.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Toolbar */}
          <div className="bg-card border-b border-border px-6 py-3 flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input type="text" placeholder="Search jobs, customers..." value={searchQuery}
                onChange={e => setSearchQuery(e?.target?.value)}
                className="pl-9 pr-4 py-2 text-sm border border-border rounded-lg bg-muted text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary w-52 placeholder:text-muted-foreground" />
            </div>
            <select value={filterStatus} onChange={e => setFilterStatus(e?.target?.value)}
              className="text-sm border border-border rounded-lg px-3 py-2 bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
              <option value="all">All Statuses</option>
              {Object.entries(STATUS_CONFIG)?.map(([k, v]) => <option key={k} value={k}>{v?.label}</option>)}
            </select>
            <select value={filterPriority} onChange={e => setFilterPriority(e?.target?.value)}
              className="text-sm border border-border rounded-lg px-3 py-2 bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
              <option value="all">All Priorities</option>
              {Object.entries(PRIORITY_CONFIG)?.map(([k, v]) => <option key={k} value={k}>{v?.label}</option>)}
            </select>
            <select value={filterTech} onChange={e => setFilterTech(e?.target?.value)}
              className="text-sm border border-border rounded-lg px-3 py-2 bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
              <option value="all">All Technicians</option>
              <option value="unassigned">Unassigned</option>
              {technicians?.map(t => <option key={t?.id} value={t?.id}>{t?.name}</option>)}
            </select>

            <div className="ml-auto flex items-center bg-muted rounded-xl p-1 gap-1">
              {[{ key: 'board', icon: 'Columns', label: 'Board' }, { key: 'map', icon: 'Map', label: 'Map' }, { key: 'list', icon: 'List', label: 'List' }]?.map(v => (
                <button key={v?.key} onClick={() => setActiveView(v?.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${activeView === v?.key ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                  <Icon name={v?.icon} size={13} />
                  {v?.label}
                </button>
              ))}
            </div>

            <button onClick={() => setShowTechManage(true)}
              className="flex items-center gap-2 px-3 py-2 border border-border rounded-xl text-sm font-medium text-foreground hover:bg-muted transition-colors">
              <Icon name="Users" size={15} />
              Technicians
            </button>
            <button onClick={() => setShowAddJobModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors shadow-sm">
              <Icon name="Plus" size={15} />
              New Job
            </button>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="w-8 h-8 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {/* BOARD VIEW */}
                {activeView === 'board' && (
                  <div className="h-full overflow-x-auto overflow-y-hidden">
                    <div className="flex gap-4 p-5 h-full" style={{ minWidth: 'max-content' }}>
                      <UnassignedColumn
                        jobs={filteredJobs}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onJobClick={setSelectedJob}
                        draggingJobId={draggingJob?.id}
                      />
                      {technicians?.map(tech => (
                        <TechnicianColumn
                          key={tech?.id}
                          tech={tech}
                          jobs={filteredJobs}
                          onDragStart={handleDragStart}
                          onDragEnd={handleDragEnd}
                          onDragOver={handleDragOver}
                          onDrop={handleDrop}
                          onJobClick={setSelectedJob}
                          draggingJobId={draggingJob?.id}
                        />
                      ))}
                      {technicians?.length === 0 && (
                        <div className="flex flex-col items-center justify-center w-64 border-2 border-dashed border-border rounded-2xl text-muted-foreground p-8">
                          <Icon name="Users" size={28} className="mb-3" />
                          <p className="text-sm font-medium text-center mb-3">No technicians yet</p>
                          <button onClick={() => setShowAddTechModal(true)}
                            className="px-4 py-2 bg-primary text-white rounded-lg text-xs font-medium hover:bg-primary/90 transition-colors">
                            Add Technician
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* MAP VIEW */}
                {activeView === 'map' && (
                  <div className="h-full overflow-y-auto p-5">
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                      <div className="xl:col-span-2">
                        <MapView
                          jobs={jobs}
                          technicians={technicians}
                          gpsPositions={gpsPositions}
                          countdown={countdown}
                          onManualRefresh={doRefresh}
                          isRefreshing={isRefreshing}
                        />
                      </div>
                      <div className="space-y-3 overflow-y-auto" style={{ maxHeight: '460px' }}>
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-foreground">Technician Status</p>
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                            <span className="tabular-nums">Live · {countdown}s</span>
                          </div>
                        </div>
                        {technicians?.length === 0 && (
                          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                            <Icon name="Users" size={24} className="mb-2" />
                            <p className="text-sm">No technicians yet</p>
                            <button onClick={() => setShowAddTechModal(true)}
                              className="mt-3 px-4 py-2 bg-primary text-white rounded-lg text-xs font-medium hover:bg-primary/90 transition-colors">
                              Add Technician
                            </button>
                          </div>
                        )}
                        {technicians?.map(tech => {
                          const statusCfg = TECH_STATUS?.[tech?.status];
                          const techJobs = jobs?.filter(j => j?.techId === tech?.id);
                          return (
                            <div key={tech?.id} className="bg-card rounded-xl border border-border p-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold shrink-0" style={{ backgroundColor: tech?.color }}>
                                  {tech?.avatar}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="text-sm font-semibold text-foreground truncate">{tech?.name}</p>
                                  </div>
                                  <p className="text-xs text-muted-foreground">{tech?.role} · {techJobs?.length} jobs</p>
                                </div>
                                <div className="flex gap-1 shrink-0">
                                  {tech?.phone && (
                                    <a href={`tel:${tech?.phone}`} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
                                      <Icon name="Phone" size={13} className="text-muted-foreground" />
                                    </a>
                                  )}
                                </div>
                              </div>
                              {/* Status selector */}
                              <div className="mt-2 flex gap-1 flex-wrap">
                                {Object.entries(TECH_STATUS)?.map(([k, v]) => (
                                  <button key={k} onClick={() => handleTechStatusChange(tech?.id, k)}
                                    className={`text-xs px-2 py-0.5 rounded-full font-medium transition-colors ${tech?.status === k ? `${v?.bg} ${v?.text}` : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
                                    {v?.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* LIST VIEW */}
                {activeView === 'list' && (
                  <div className="h-full overflow-y-auto p-5">
                    <div className="bg-card rounded-2xl border border-border overflow-hidden">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-muted border-b border-border">
                            <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Job</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Customer</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Service</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Priority</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Technician</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Time</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Value</th>
                            <th className="px-4 py-3" />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {filteredJobs?.map(job => {
                            const priority = PRIORITY_CONFIG?.[job?.priority] || PRIORITY_CONFIG?.normal;
                            const status = STATUS_CONFIG?.[job?.status] || STATUS_CONFIG?.unassigned;
                            const tech = technicians?.find(t => t?.id === job?.techId);
                            return (
                              <tr key={job?.id} className="hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => setSelectedJob(job)}>
                                <td className="px-4 py-3">
                                  <div className={`flex items-center gap-2 border-l-4 pl-2 ${priority?.border}`}>
                                    <div>
                                      <p className="text-sm font-semibold text-foreground">{job?.title}</p>
                                      <p className="text-xs text-muted-foreground truncate max-w-[180px]">{job?.address}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <p className="text-sm text-foreground">{job?.customer}</p>
                                  <p className="text-xs text-muted-foreground">{job?.phone}</p>
                                </td>
                                <td className="px-4 py-3">
                                  <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-md font-medium">{job?.service}</span>
                                  {job?.moveType === 'long_distance' && (
                                    <span className="ml-1 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 px-1.5 py-0.5 rounded-md font-medium">LD</span>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`text-xs font-medium px-2 py-1 rounded-md border ${priority?.color}`}>{priority?.label}</span>
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`text-xs font-medium px-2 py-1 rounded-md ${status?.color}`}>{status?.label}</span>
                                </td>
                                <td className="px-4 py-3">
                                  {tech ? (
                                    <div className="flex items-center gap-2">
                                      <div className="w-6 h-6 rounded-md flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: tech?.color }}>
                                        {tech?.avatar?.charAt(0)}
                                      </div>
                                      <span className="text-xs text-foreground font-medium">{tech?.name?.split(' ')?.[0]}</span>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-red-500 dark:text-red-400 font-medium">Unassigned</span>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  <p className="text-xs text-foreground font-medium">{job?.scheduledTime}</p>
                                  {job?.scheduledDate && <p className="text-xs text-muted-foreground">{job?.scheduledDate}</p>}
                                </td>
                                <td className="px-4 py-3">
                                  <span className="text-sm font-semibold text-green-600 dark:text-green-400">{job?.value}</span>
                                </td>
                                <td className="px-4 py-3">
                                  <button className="p-1.5 rounded-lg hover:bg-muted transition-colors">
                                    <Icon name="ChevronRight" size={15} className="text-muted-foreground" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {filteredJobs?.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                          <Icon name="SearchX" size={32} className="mb-3" />
                          <p className="text-sm font-medium">No jobs match your filters</p>
                          <button onClick={() => { setFilterStatus('all'); setFilterPriority('all'); setFilterTech('all'); setSearchQuery(''); }}
                            className="mt-3 text-xs text-primary hover:underline">Clear filters</button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {/* Technician Management Panel */}
      {showTechManage && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={() => setShowTechManage(false)} />
          <div className="w-[420px] bg-card h-full shadow-2xl flex flex-col border-l border-border">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h2 className="text-base font-semibold text-foreground">Manage Technicians</h2>
              <div className="flex items-center gap-2">
                <button onClick={() => { setShowTechManage(false); setShowAddTechModal(true); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-medium hover:bg-primary/90 transition-colors">
                  <Icon name="Plus" size={13} />
                  Add
                </button>
                <button onClick={() => setShowTechManage(false)} className="p-2 rounded-lg hover:bg-muted transition-colors">
                  <Icon name="X" size={18} className="text-muted-foreground" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {technicians?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Icon name="Users" size={28} className="mb-3" />
                  <p className="text-sm">No technicians yet</p>
                  <button onClick={() => { setShowTechManage(false); setShowAddTechModal(true); }}
                    className="mt-3 px-4 py-2 bg-primary text-white rounded-lg text-xs font-medium hover:bg-primary/90 transition-colors">
                    Add First Technician
                  </button>
                </div>
              ) : (
                technicians?.map(tech => {
                  const statusCfg = TECH_STATUS?.[tech?.status] || TECH_STATUS?.offline;
                  const techJobs = jobs?.filter(j => j?.techId === tech?.id);
                  return (
                    <div key={tech?.id} className="bg-background border border-border rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold shrink-0" style={{ backgroundColor: tech?.color }}>
                          {tech?.avatar}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-foreground truncate">{tech?.name}</p>
                          </div>
                          <p className="text-xs text-muted-foreground">{tech?.role} · ⭐ {tech?.rating} · {techJobs?.length} jobs</p>
                          {tech?.phone && <p className="text-xs text-muted-foreground">{tech?.phone}</p>}
                          {tech?.skills?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {tech?.skills?.slice(0, 3)?.map(s => (
                                <span key={s} className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">{s}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <button onClick={() => { setEditingTech(tech); setShowTechManage(false); }}
                            className="p-1.5 rounded-lg hover:bg-muted transition-colors">
                            <Icon name="Pencil" size={13} className="text-muted-foreground" />
                          </button>
                          <button onClick={() => setConfirmDeleteTech(tech)}
                            className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                            <Icon name="Trash2" size={13} className="text-red-500" />
                          </button>
                        </div>
                      </div>
                      {/* Quick status change */}
                      <div className="mt-3 flex gap-1 flex-wrap">
                        {Object.entries(TECH_STATUS)?.map(([k, v]) => (
                          <button key={k} onClick={() => handleTechStatusChange(tech?.id, k)}
                            className={`text-xs px-2 py-0.5 rounded-full font-medium transition-colors ${tech?.status === k ? `${v?.bg} ${v?.text} ring-1 ring-current` : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
                            {v?.label}
                          </button>
                        ))}
                      </div>
                      {confirmDeleteTech?.id === tech?.id && (
                        <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                          <p className="text-xs text-red-700 dark:text-red-400 mb-2">Remove {tech?.name}?</p>
                          <div className="flex gap-2">
                            <button onClick={() => setConfirmDeleteTech(null)} className="flex-1 py-1 border border-border rounded text-xs hover:bg-muted transition-colors">Cancel</button>
                            <button onClick={() => handleDeleteTech(tech?.id)} className="flex-1 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 transition-colors">Remove</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
      {/* Modals */}
      {showAddJobModal && (
        <JobModal job={null} technicians={technicians} orgId={profile?.org_id} onClose={() => setShowAddJobModal(false)} onSaved={fetchData} />
      )}
      {editingJob && (
        <JobModal job={editingJob} technicians={technicians} orgId={profile?.org_id} onClose={() => setEditingJob(null)} onSaved={() => { fetchData(); setSelectedJob(null); }} />
      )}
      {showAddTechModal && (
        <TechnicianModal tech={null} orgId={profile?.org_id} onClose={() => setShowAddTechModal(false)} onSaved={fetchData} />
      )}
      {editingTech && (
        <TechnicianModal tech={editingTech} orgId={profile?.org_id} onClose={() => setEditingTech(null)} onSaved={fetchData} />
      )}
      {/* Job Detail Drawer */}
      {selectedJob && (
        <JobDetailDrawer
          job={selectedJob}
          technicians={technicians}
          onClose={() => setSelectedJob(null)}
          onAssign={handleAssign}
          onStatusChange={handleStatusChange}
          onEdit={(job) => { setEditingJob(job); setSelectedJob(null); }}
          onDelete={handleDeleteJob}
        />
      )}
    </div>
  );
};

export default DispatchPage;
